import { FC, useMemo } from 'react'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { schemaNumber, schemaString } from 'schemas/baseSchema'
import { FormPreset } from 'components/atoms/inputs/FormPreset/FormPreset'
import { LabeledInput } from 'components/atoms/inputs/LabeledInput/LabeledInput'
import Button from 'components/features/pfm/Button/Button'
import Flex from 'components/atoms/Flex/Flex'
import { TYPE_REQUIRED } from 'constants/errors'

type ExampleRadioOptions = 'radioOption1' | 'radioOption2' | 'radioOption3' | 'radioOption4'
type ExampleCheckboxOptions = 'checkboxOption1' | 'checkboxOption2' | 'checkboxOption3' | 'checkboxOption4'
type ExampleSelectOptions = 'selectOption1' | 'selectOption2' | 'selectOption3' | 'selectOption4'

export const schemaRadioOptions = yup.string().required(TYPE_REQUIRED).oneOf([
  'radioOption1',
  'radioOption2',
  'radioOption3',
  'radioOption4',
], TYPE_REQUIRED)

export const schemaCheckboxOptions = yup.string().required(TYPE_REQUIRED).oneOf([
  'checkboxOption1',
  'checkboxOption2',
  'checkboxOption3',
  'checkboxOption4',
], TYPE_REQUIRED)

export const schemaSelectOptions = yup.string().required(TYPE_REQUIRED).oneOf([
  'selectOption1',
  'selectOption2',
  'selectOption3',
  'selectOption4',
], TYPE_REQUIRED)

export type ExampleFormType = {
  'numeric': string
  'currency': string
  'credit_card': string
  'zip_code': string
  'percent': number
  'phone': string
  'text': string
  'switch': boolean
  'checkbox': boolean
  'checkboxes': ExampleCheckboxOptions
  'radio': ExampleRadioOptions
  'select': ExampleSelectOptions
}

const SGFormFields: FC<{}> = () => {

  const exampleFormValidator = yup.object<ExampleFormType>({
    numeric: schemaString,
    currency: schemaString,
    credit_card: schemaString,
    zip_code: schemaString,
    percent: schemaNumber,
    phone: schemaString,
    text: schemaString,
    switch: yup.boolean(),
    checkbox: yup.boolean(),
    checkboxes: schemaCheckboxOptions,
    radio: schemaRadioOptions,
    select: schemaSelectOptions,
  })

  const formContext = useForm<ExampleFormType>({
    reValidateMode: 'onChange',
    resolver: yupResolver(exampleFormValidator),
    defaultValues: {
      numeric: '123',
      currency: '123456789',
      credit_card: '',
      zip_code: '',
      percent: 0.5,
      phone: '',
      text: '',
      switch: true,
      checkbox: true,
      checkboxes: 'checkboxOption1',
      radio: 'radioOption2',
      select: 'selectOption1',
    },
  })

  const handleSubmit = useMemo(() => formContext.handleSubmit((values) => {
    alert(values.credit_card)
  }), [formContext])
  return (
    <FormPreset formContext={ formContext } onSubmit={ handleSubmit } debug>
      <Flex horizontal responsive>
        <Flex padding='large'>
          <Button text='Submit Form' submit />
          <h2>Text Fields</h2>
          <LabeledInput
            label='Default'
            name='default'
            note='My default helper note lorem Aute tempor proident cillum voluptate irure nulla proident ipsum voluptate reprehenderit incididunt exercitation dolore occaecat.'
          />
          <LabeledInput
            label='Text'
            name='text'
            type='text'
            placeholder='Jane Doe'
            note='My text helper note lorem Aute tempor proident cillum voluptate irure nulla proident ipsum voluptate reprehenderit incididunt exercitation dolore occaecat.'
          />
          <LabeledInput
            label='Email'
            name='email'
            type='email'
            placeholder='jane.doe@google.com'
            note='My email helper note lorem Aute tempor proident cillum voluptate irure nulla proident ipsum voluptate reprehenderit incididunt exercitation dolore occaecat.'
          />
          <LabeledInput
            label='Number'
            name='number'
            type='number'
            note='My number helper noteirure nulla proident ipsum voluptate reprehend irure nulla proident ipsum voluptate reprehend'
            units='months'
          />
          <LabeledInput
            label='Currency'
            name='currency'
            type='currency'
            note='My currency helper note'
          />
          <LabeledInput
            label='Credit Card'
            name='credit_card'
            type='credit_card'
            note='My credit_card helper note'
          />
          <LabeledInput
            label='Percent'
            name='percent'
            type='percent'
            note='My percent helper note'
          />
          <LabeledInput
            label='Zip Code'
            name='zip_code'
            type='zip_code'
            note='My zip_code helper note'
          />
          <LabeledInput
            label='Phone'
            name='phone'
            type='phone'
            note='My phone helper note'
          />
        </Flex>

        <Flex padding='large' gap='large'>
          <h2>Other Inputs</h2>
          <LabeledInput
            label='Select'
            name='select'
            type='select'
            note='My Select note'
            options={ [
              { label: 'Option 1', value: 'selectOption1' },
              { label: 'Option 2', value: 'selectOption2' },
              { label: 'Option 3', value: 'selectOption3' },
              { label: 'Option 4', value: 'selectOption4' },
            ] }
          />
          <LabeledInput
            label='Switch'
            name='switch'
            type='switch'
            note='My switch helper note'
          />
          <LabeledInput
            label='Checkbox'
            name='checkbox'
            type='checkbox'
            note='My boolean checkbox helper note'
          />
          <LabeledInput
            label='Checkboxes'
            name='checkboxes'
            type='checkboxes'
            note='My checkboxes note'
            options={ [
              { label: 'Option 1', value: 'checkboxOption1' },
              { label: 'Option 2', value: 'checkboxOption2' },
              { label: 'Option 3', value: 'checkboxOption3' },
              { label: 'Option 4', value: 'checkboxOption4' },
            ] }
          />
          <LabeledInput
            label='Account Type'
            name='radio'
            type='radio'
            horizontal
            style='bare'
            options={ [
              { label: 'Option 1', value: 'radioOption1' },
              { label: 'Option 2', value: 'radioOption2' },
            ] }
          />
          <LabeledInput
            label='Radio Button'
            name='radio'
            type='radio'
            note='My radio buttons note'
            options={ [
              { label: 'Option 1', value: 'radioOption1', note: 'Some helper note about Option 1' },
              { label: 'Option 2', value: 'radioOption2', note: 'Some helper note about Option 2' },
              { label: 'Option 3', value: 'radioOption3', note: 'Some helper note about Option 3' },
              { label: 'Option 4', value: 'radioOption4', note: 'Some helper note about Option 4' },
            ] }
          />
        </Flex>
      </Flex>
    </FormPreset>
  )
}

export default SGFormFields
